import { CSSObject, DefaultTheme, ThemeProvider, createGlobalStyle, css } from 'styled-components'

import { BASE_THEME_STYLES } from './mode-themes'
import { Colors } from './styled'
import { SkeletonTheme } from 'react-loading-skeleton'
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo } from 'react'
import { useStore } from 'services/store'

export interface MediaWidth {
  Mobile: { from: number; to: number }
  Tablet: { from: number; to: number }
  Small: { from: number; to: number }
  Desktop: { from: number; to: number }
}

export const MEDIA_WIDTHS: any = {
  Small: { from: 0, to: 1023 },
  Mobile: { from: 0, to: 759 },
  Tablet: { from: 760, to: 1023 },
  Desktop: { from: 1024, to: 8000 },
}

const theme = (darkMode: boolean): DefaultTheme => {
  return {
    ...colors(darkMode),
    mediaWidth: mediaWidthTemplates,
    darkMode,
  }
}

const mediaWidthTemplates: any = Object.keys(MEDIA_WIDTHS).reduce(
  (acc: any, screen) => {
    acc[screen] = (a: CSSObject, b: number, c: number) => css`
      @media (min-width: ${MEDIA_WIDTHS[screen].from}px) and (max-width: ${MEDIA_WIDTHS[screen]
          .to}px) {
        ${css(a, b, c)}
      }
    `
    return acc
  },
  {} as Record<string, string>,
)

export const GLOBAL_COLORS = {
  // base
  black: '#000000',
  white: '#ffffff',
  customBlack: '#111111',
  transparent: 'transparent',
  greySlate: '#707070',
  stoneGrey: '#B8B8B8',
  whitepaperGreen: '#2DC798',
  investBackgroundLight: '#f6f8ff',
  investBackgroundDark: '#292929',
  //primary
  black05: '#00000080',
  deepOceanBlue: '#000725',
  deepOceanBlue03: '#0007254D',
  deepOceanBlue05: '#00072580',
  coralBlue: '#0038FF',
  coralBlue1: '#0038FFff',
  ultramarine: '#3E1FFF',
  ultramarine0: '#3E1FFF00',
  darkUltramarine: '#1D00D1',
  rurikonBlue: '#172443',
  cautiousBlue: '#D3DCE4',
  seashellWhite: '#F1F5F6',
  seashellWhite03: '#F1F5F64D',
  seashellWhite05: '#F1F5F680',
  seashellWhite07: '#F1F5F6B3',
  coreGrey: '#606060',
  coreGrey05: '#60606080',
  lightBlue: '#E0E8ED',
  lightBlue05: '#E0E8ED80',
  secondaryBlue: '#061331',
  darkBlue: '#0E1C3C',
  widgetDark: '#071231',
  secondaryGreen: '#00B576',
  denim: '#305166',
  denim03: '#3051664D',
  walletLabelGrey: '#E8EEF1',
  chartLineGrey: '#666666',
  dividerGray: '#999999',

  //secondary
  tidalBlue: '#487199',
  tidalBlue07: '#27313ab3',
  tidalBlue01: '#4871991A',
  secondGrey: '#ACACAC',
  secondGrey02: '#ACACAC33',
  secondGrey05: '#ACACAC80',
  pastelGreen: '#D6EADF',
  shoreBlue: '#E9FCFF',
  duskPurple: '#E9F4FF',
  canopyGreen: '#01D28A',
  volcanoRed: '#E24242',
  widgetBorder: '#CFD0D1',
  yieldChartOrange: '#F4774F',
  amberYellow: '#FFD338',

  // accents
  accentGrey: '#787878',

  //gradients
  phutureGradient1: 'linear-gradient(102.87deg, #0038FF -2.03%, #001259 111.45%)',
  phutureGradient2: 'linear-gradient(113.79deg, #00A8E8 -2.09%, #3454D1 79.6%)',

  //wallet button
  walletButton: '#7B99B5',
  walletHover: '#D9E3E8',

  //block explorer colors
  blockExplorerDark: '#0C0C0C',

  //product colors
  pulsePurple: '#7A57DE',

  // Footer border
  footerBorder: '#d5d5d5',
  badgeLinkDark: '#8f8f8f',
  badgeLinkLight: '#000',
  badgeHoverLight: '#0138ff',
  badgeHoverDark: '#ffffff',

  //account center
  accountCenterBgLight: 'rgba(255, 255, 255, 0.9)',
  accountCenterBgDark: 'rgba(30, 30, 30, 0.9)',

  //nested menu
  nestedMenuHoverLight: '#f7f8ff',
  nestedMenuHoverDark: '#000',

  //invest-component
  investTabGrey: '#CFCFCF',
  investCurrencyGrey: '#a0a0a0',

  //separator
  separatorBg: '#d9d9d9',

  //switch section
  switchBlue: '#f6f8ff',
  switchGrey: '#2D2D2D',
}

export const colors = (darkMode: boolean): Colors => {
  const themedStyle = darkMode ? BASE_THEME_STYLES.dark : BASE_THEME_STYLES.light
  return {
    //base
    white: GLOBAL_COLORS.white,
    black: GLOBAL_COLORS.black,
    customBlack: GLOBAL_COLORS.customBlack,
    greySlate: darkMode ? GLOBAL_COLORS.stoneGrey : GLOBAL_COLORS.greySlate,

    // theme polymorph
    whiteToCoreGrey: darkMode ? GLOBAL_COLORS.white : GLOBAL_COLORS.coreGrey,
    seashellWhiteToCoreGrey: darkMode ? GLOBAL_COLORS.seashellWhite : GLOBAL_COLORS.coreGrey,
    seashellWhite05ToCoreGrey: darkMode ? GLOBAL_COLORS.seashellWhite05 : GLOBAL_COLORS.coreGrey,
    black05ToDenim03: darkMode ? GLOBAL_COLORS.black05 : GLOBAL_COLORS.denim03,
    seashellWhite07ToDeepOceanBlue: darkMode
      ? GLOBAL_COLORS.seashellWhite07
      : GLOBAL_COLORS.deepOceanBlue,
    seashellWhiteToDeepOceanBlue: darkMode
      ? GLOBAL_COLORS.seashellWhite
      : GLOBAL_COLORS.deepOceanBlue,
    whiteToSecondGrey: darkMode ? GLOBAL_COLORS.white : GLOBAL_COLORS.secondGrey,
    darkBlueToLightBlue: darkMode ? GLOBAL_COLORS.darkBlue : GLOBAL_COLORS.lightBlue,
    darkBlueToWhite: darkMode ? GLOBAL_COLORS.darkBlue : GLOBAL_COLORS.white,
    coreGreyToSecondGrey: darkMode ? GLOBAL_COLORS.coreGrey : GLOBAL_COLORS.secondGrey,
    coreGrey05ToSecondGrey05: darkMode ? GLOBAL_COLORS.coreGrey05 : GLOBAL_COLORS.secondGrey05,
    lightBlue05ToTidalBlue01: darkMode ? GLOBAL_COLORS.tidalBlue01 : GLOBAL_COLORS.lightBlue05,
    coralBlueToWhite: darkMode ? GLOBAL_COLORS.white : GLOBAL_COLORS.coralBlue,
    whiteToBlack: darkMode ? GLOBAL_COLORS.black : GLOBAL_COLORS.white,
    investmentBackground: darkMode
      ? GLOBAL_COLORS.investBackgroundDark
      : GLOBAL_COLORS.investBackgroundLight,
    // inverted icon border color
    deepOceanBlueToSeashellWhite: darkMode
      ? GLOBAL_COLORS.deepOceanBlue
      : GLOBAL_COLORS.seashellWhite,

    // text
    text1: darkMode ? GLOBAL_COLORS.white : GLOBAL_COLORS.black,
    secondaryText: darkMode ? '#B8B8B8' : `${GLOBAL_COLORS.black}99`,

    // icon
    iconColor: darkMode ? GLOBAL_COLORS.white : GLOBAL_COLORS.black,
    inverseIconColor: darkMode ? GLOBAL_COLORS.black : GLOBAL_COLORS.white,
    iconHoverColor: darkMode ? GLOBAL_COLORS.seashellWhite : GLOBAL_COLORS.transparent,

    productBadgeStrokeColor: darkMode ? GLOBAL_COLORS.badgeLinkDark : GLOBAL_COLORS.badgeHoverLight,
    productBadgeHoverStrokeColor: darkMode
      ? GLOBAL_COLORS.badgeHoverDark
      : GLOBAL_COLORS.badgeHoverLight,

    //secondary button
    secondaryButtonText: darkMode ? GLOBAL_COLORS.white : GLOBAL_COLORS.black,

    // checkbox
    checkboxBorder: darkMode ? GLOBAL_COLORS.white : GLOBAL_COLORS.deepOceanBlue,

    inverseBg: darkMode ? GLOBAL_COLORS.seashellWhite : GLOBAL_COLORS.deepOceanBlue,
    widgetBG: darkMode ? GLOBAL_COLORS.widgetDark : GLOBAL_COLORS.lightBlue,

    //input
    inputColor: darkMode ? GLOBAL_COLORS.white : GLOBAL_COLORS.black,
    inputPlaceholderColor: darkMode ? GLOBAL_COLORS.seashellWhite05 : GLOBAL_COLORS.deepOceanBlue05,

    //table
    titleColor: darkMode ? GLOBAL_COLORS.seashellWhite07 : GLOBAL_COLORS.coreGrey,

    //modal
    modalBlurColor: darkMode ? GLOBAL_COLORS.deepOceanBlue05 : GLOBAL_COLORS.seashellWhite05,
    transactionBorderColor: darkMode
      ? GLOBAL_COLORS.seashellWhite03
      : GLOBAL_COLORS.deepOceanBlue03,

    //block explorer badge colors
    blockExplorerDarkToLight: darkMode ? GLOBAL_COLORS.white : GLOBAL_COLORS.blockExplorerDark,

    // new themes
    bg: themedStyle.background,
    boxShadow: themedStyle.boxShadow,
    primaryFontColor: themedStyle.primaryFontColor,
    secondaryFontColor: themedStyle.secondaryFontColor,
    connectColor: themedStyle.connectColor,
    borderColor: themedStyle.borderColor,
    activeItemColor: themedStyle.active,
    retryLinkColor: themedStyle.retryLinkColor,

    // list highlight accent for notifications (failed)
    highlightAccent: themedStyle.highlightAccent,
    skeleton: themedStyle.skeleton,
    skeletonHighlight: themedStyle.skeletonHighlight,

    //account center
    accountCenterBg: darkMode
      ? GLOBAL_COLORS.accountCenterBgDark
      : GLOBAL_COLORS.accountCenterBgLight,

    nestedMenuHover: darkMode
      ? GLOBAL_COLORS.nestedMenuHoverDark
      : GLOBAL_COLORS.nestedMenuHoverLight,

    //switch section
    switchBgColor: darkMode ? GLOBAL_COLORS.switchGrey : GLOBAL_COLORS.switchBlue,
    switchTextColor: darkMode ? GLOBAL_COLORS.white : GLOBAL_COLORS.coralBlue,

    //widget bg colors
    widgetBgColor: darkMode ? GLOBAL_COLORS.switchGrey : GLOBAL_COLORS.white,
  }
}

export function CustomThemeProvider({ children }: { children: any }): JSX.Element {
  const { isDarkMode } = useStore()

  const themeObject = useMemo(() => theme(isDarkMode), [isDarkMode])

  return (
    <ThemeProvider theme={themeObject}>
      <SkeletonTheme
        baseColor={themeObject.skeleton}
        highlightColor={themeObject.skeletonHighlight}
      >
        <>{children}</>
      </SkeletonTheme>
    </ThemeProvider>
  )
}

export const ThemedGlobalStyle = createGlobalStyle`
html {
  color: ${({ theme }) => theme.text1};
  background-color: ${({ theme }) => theme.bg} !important;
}`
